<script setup lang="ts">
import {
  CustomFieldType,
  CustomFieldTypeValues,
} from '@/lib/request/types/timesheetSetup';
import { getTranslations } from '@/lib/util/i18n';
import { computed, reactive } from 'vue';
import { CoInput, CoRadio } from '@deputy/copilot2';
import Multiselect from '@/lib/multiselect/multiselect';

export interface PropsTypes {
  type: CustomFieldTypeValues;
  modelValue: any;
  valueOptions?: string[];
  questionText?: string;
  helpText?: string;
  placeholderText?: string;
  required?: boolean;
  showError?: boolean;
}

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'shift-question',
});

const props = withDefaults(defineProps<PropsTypes>(), {
  placeholderText: '',
});

const emit = defineEmits(['update:modelValue']);

const state = reactive({
  value: computed({
    get: () => {
      if (props.type == CustomFieldType.MultiList) {
        return (props.modelValue || []).map((value: string) => {
          return { value: value, label: value };
        });
      }
      return props.modelValue;
    },
    set: (value) => {
      if (props.type == CustomFieldType.MultiList) {
        emit(
          'update:modelValue',
          value.map((obj: Record<string, string>) => {
            return obj.value;
          })
        );
        return;
      }
      emit('update:modelValue', value);
    },
  }),
});

const stateCheckbox = reactive({ value: '' });
if (
  props.type == CustomFieldType.BooleanCheckbox ||
  props.type == CustomFieldType.BooleanCheckboxYesWithComment ||
  props.type == CustomFieldType.BooleanCheckboxNoWithComment
) {
  if (props.modelValue.toString() == 'true') {
    stateCheckbox.value = 'yes';
  } else if (props.modelValue.toString() == 'false') {
    stateCheckbox.value = 'no';
  }
}
const handleCheckboxChange = (value: string) => {
  emit('update:modelValue', (value === 'yes').toString());
};

const isError = computed(
  () =>
    props.showError && props.required && (!state.value || !state.value?.length)
);

let inputType = '';
switch (props.type) {
  case CustomFieldType.Number:
    inputType = 'number';
    break;
  case CustomFieldType.LargeText:
    inputType = 'textarea';
    break;
}
</script>

<template>
  <div class="__shift-question" :class="{ 'show-error': isError }">
    <div class="__question-text">
      {{ questionText }}{{ required ? '*' : '' }}
    </div>
    <div class="__help-text">{{ helpText }}</div>

    <div class="__field">
      <CoInput
        v-if="
          type == CustomFieldType.Text ||
          type == CustomFieldType.Number ||
          type == CustomFieldType.LargeText
        "
        :type="inputType"
        v-model="state.value"
        :placeholder="placeholderText"
        :error="isError"
      />

      <div
        v-if="
          type == CustomFieldType.BooleanCheckbox ||
          type == CustomFieldType.BooleanCheckboxYesWithComment ||
          type == CustomFieldType.BooleanCheckboxNoWithComment
        "
      >
        <CoRadio
          type="pill"
          v-model="stateCheckbox.value"
          label="yes"
          @change="handleCheckboxChange"
          >{{ t('boolean-yes') }}</CoRadio
        >
        <CoRadio
          type="pill"
          v-model="stateCheckbox.value"
          label="no"
          @change="handleCheckboxChange"
          >{{ t('boolean-no') }}</CoRadio
        >
      </div>
      <Multiselect
        v-if="type == CustomFieldType.List"
        mode="single"
        v-model="state.value"
        :options="props.valueOptions"
      />
      <Multiselect
        v-if="type == CustomFieldType.MultiList"
        mode="tags"
        v-model="state.value"
        :close-on-select="false"
        :searchable="true"
        :object="true"
        :options="props.valueOptions"
      />
    </div>
    <div v-if="isError" class="__error-message">{{ t('error-required') }}</div>
  </div>
</template>

<style src="@/lib/multiselect/default.css"></style>

<style scoped>
.__shift-question {
  margin-bottom: 32px;
}
.__question-text {
  font-size: 16px;
  line-height: 24px;
}
.__help-text {
  font-size: 14px;
  line-height: 20px;
  color: var(--co-text-secondary);
}
.__field {
  margin-top: 8px;
}
.__error-message {
  color: var(--co-text-attention);
  margin-top: 5px;
}
</style>
<style>
.__shift-question.show-error .co-textarea__inner,
.__shift-question.show-error .co-radio--pill .co-radio--pill__inner,
.__shift-question.show-error .multiselect {
  border: 1px solid var(--co-status-red);
}
.__shift-question .co-radio--pill__label {
  top: 4px;
}
.__shift-question .multiselect-tag {
  color: var(--co-text-primary);
  background-color: var(--co-button-secondary-fill);
  font-weight: 400;
}
.__shift-question .multiselect {
  border-radius: 8px;
}
.__shift-question .multiselect.is-active {
  outline: none;
  border-color: var(--co-text-highlight);
  box-shadow: none;
}
.__shift-question .multiselect-option.is-selected {
  color: var(--co-text-highlight);
  background-color: transparent;
}
.__shift-question .multiselect-option.is-pointed {
  background-color: var(--co-background-secondary);
}
.__shift-question .multiselect-option.is-selected span {
  font-weight: bold;
}
</style>
